import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { useParams, useLoaderData, Link } from 'react-router-dom';
import { redirectErrorPage } from '../../utils/errors';
import NivoLineChart from '../../components/charts/NivoLineChart';
import { BasicTooltip, TableTooltip, Chip } from '@nivo/tooltip';
import { useTranslation } from 'react-i18next';
import ContentSection from '../../components/common/ContentSection/ContentSection';
import StatChip from '../../components/common/StatChip/StatChip';
import { capitalizeFirstLetter, useFormatters } from '../../utils/texts';
import { getColors } from '../../Constants';
import MemberCard from '../../components/common/MemberCard/MemberCard';
import VoteBar from '../../components/common/VoteBar/VoteBar';
import MeetingAttendanceBar from '../../components/common/MeetingAttendanceBar/MeetingAttendanceBar';
import { getApiClient } from '../../api';
import { useVoteColors } from '../../utils/hooks';
import { sparseTickValues, estimateAxisLeftMargin, niceIntegerTicks } from '../../utils/nivoTicks';
import { truncateLegendLabel } from '../../utils/nivoTheme';
import type { PartyData, PartyAgreementItem } from '../../api';
import { useLocalizationLabels } from '../../context/LocalizationLabelContext';
import { useMunicipalityId, useCouncilPath } from '../../hooks/useCouncilPath';
import { useAppContext } from '../../context/AppContext';
import { formatDateRange } from '../../utils/dates';
import { Skeleton } from '../../components/PrimereactComponents';

function PartyDetail() {
    const { t, i18n } = useTranslation(['parties', 'translation', 'meetings']);
    const { bodyType, zastupitelstvoId } = useParams();
    const municipalitaId = useMunicipalityId();
    const partyData = useLoaderData() as PartyData;

    const voteColors = useVoteColors();
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);
    const { municipalityData } = useAppContext();
    const timeZone = municipalityData.casova_zona;
    const councilPath = useCouncilPath();

    const {voteOptionLabels} = useLocalizationLabels();

    const [agreementWithOthers, setAgreementWithOthers] = useState<PartyAgreementItem[]>();

    useEffect(() => {
        if (!municipalitaId || !bodyType || !zastupitelstvoId) return;
        setAgreementWithOthers(undefined);

        let cancelled = false;
        const api = getApiClient();
        api.getAgreementByMunicipalityIdAndBodyAndCouncilIdAndPartyId(
            municipalitaId,
            bodyType,
            parseInt(zastupitelstvoId, 10),
            partyData.strana_id
        )
            .then((data) => {
                if (!cancelled) setAgreementWithOthers(data);
            })
            .catch((error) => console.error('Error fetching party agreement data:', error));

        return () => {
            cancelled = true;
        };
    }, [partyData.strana_id, municipalitaId, bodyType, zastupitelstvoId]);

    if (partyData !== undefined) {
        if (!partyData?.strana_id) {
            redirectErrorPage(municipalitaId, zastupitelstvoId);
        } else {
            const activeMembers = partyData.clenove.filter((member) => member.aktivni);
            const inactiveMembers = partyData.clenove.filter((member) => !member.aktivni);
            const summaryVotesEntries = partyData.celkove_hlasy ?? [];
            const getVoteLabel = (item: { id: number; code: string }) =>
                capitalizeFirstLetter(voteOptionLabels[item.id]?.label_universal ?? item.code);

            const cohesion = partyData.cohesion;
            const cohesionSeries = [
                {
                    id: partyData.strana_zkratka,
                    data: (cohesion?.vyvoj ?? []).map((item) => ({ x: item.zasedani, y: item.hodnota ?? null }))
                }
            ];

            const cohesionLabels = (cohesion?.vyvoj ?? []).map((item) => item.zasedani);
            // Nivo renders one x-axis tick per meeting unconditionally, which overlaps into
            // unreadable text once a term has many meetings (e.g. European Parliament data) -
            // thin the ticks and angle the survivors, same fix as UnityLineChart/MatchChartSection.
            const manyCohesionMeetings = cohesionLabels.length > 15;

            const cohesionValues = (cohesion?.vyvoj ?? [])
                .map((item) => item.hodnota)
                .filter((value): value is number => value !== null && value !== undefined);
            // Cohesion tends to sit in a narrow band (e.g. 80-95%) rather than spanning
            // the full 0-100 range, so a fixed 0-100 axis flattens the line into an
            // almost-straight strip - scale to the actual data instead, with a little
            // padding, clamped to the valid percentage range.
            const cohesionYMin = cohesionValues.length > 0 ? Math.max(0, Math.floor(Math.min(...cohesionValues) - 5)) : 0;
            const cohesionYMax = cohesionValues.length > 0 ? Math.min(100, Math.ceil(Math.max(...cohesionValues) + 5)) : 100;

            const cohesionTrendLabel = cohesion
                ? t(`PartyDetail.cohesionTrend${capitalizeFirstLetter(cohesion.smer)}`)
                : undefined;

            const leastCohesiveVoteId = cohesion?.min_cohesion_vote_id;
            const leastCohesiveVoteHodnota = cohesion?.min_cohesion_vote_hodnota;
            const leastCohesiveVotePredmet = cohesion?.min_cohesion_vote_predmet;
            const leastCohesiveVoteLabel =
                leastCohesiveVoteId != null
                    ? leastCohesiveVotePredmet
                        ? leastCohesiveVotePredmet.length > 70
                            ? `${leastCohesiveVotePredmet.slice(0, 70)}…`
                            : leastCohesiveVotePredmet
                        : `${cohesion?.min_cohesion_vote_zasedani} · #${cohesion?.min_cohesion_vote_cislo}`
                    : undefined;

            const seatsOverTime = partyData.mandaty_vyvoj ?? [];
            const mandateCounts = seatsOverTime.map((item) => item.pocet_mandatu);

            // Most meetings carry the same seat count as the one before, so plotting every
            // meeting draws a long flat line with no information in it. Keep only the meetings
            // where the count actually changed, plus the first (baseline) and last (so the line
            // still reaches up to the present) - the result is a proper step chart of changes.
            const seatsChangePoints = seatsOverTime.filter(
                (item, idx, arr) => idx === 0 || idx === arr.length - 1 || item.pocet_mandatu !== arr[idx - 1].pocet_mandatu
            );
            const seatsSeries = [
                {
                    id: partyData.strana_zkratka,
                    data: seatsChangePoints.map((item) => ({ x: item.zasedani, y: item.pocet_mandatu }))
                }
            ];

            const seatsLabels = seatsChangePoints.map((item) => item.zasedani);
            const manySeatsMeetings = seatsLabels.length > 15;

            const maxSeats = Math.max(1, ...mandateCounts, 0);
            const seatsTickValues = niceIntegerTicks(maxSeats);
            const seatsAxisLeftMargin = estimateAxisLeftMargin(seatsTickValues.map((v) => formatter.number.format(v)));

            const rawMandateAvg = mandateCounts.length > 0 ? mandateCounts.reduce((sum, v) => sum + v, 0) / mandateCounts.length : null;
            const rawMandateStdDev =
                rawMandateAvg !== null && mandateCounts.length > 0
                    ? Math.sqrt(mandateCounts.reduce((sum, v) => sum + (v - rawMandateAvg) ** 2, 0) / mandateCounts.length)
                    : null;
            const mandateAvg = rawMandateAvg !== null ? Math.round(rawMandateAvg * 10) / 10 : null;
            const mandateStdDev = rawMandateStdDev !== null ? Math.round(rawMandateStdDev * 10) / 10 : null;
            const hasMandateDeviation = mandateStdDev !== null && mandateStdDev > 0;

            const partyIsActiveWholeTerm = partyData.dochazka.mandat === partyData.dochazka.celkem_zasedani;
            const allMembersCountString =
                partyData.clenove.length > partyData.pocet_aktivnich
                    ?`(${t('PartyDetail.forWholeTermMembers_0', {
                          count: partyData.clenove.length
                      })})`
                    : '';
            const votingPercentageAttendance = formatter.percent2From100(partyData.attendance);

            const isInactiveParty = partyData?.pocet_aktivnich === 0;
            const titleColor = isInactiveParty ? 'var(--color-muted60)' : partyData?.strana_barva;
            const subtitleText = isInactiveParty
                ? capitalizeFirstLetter(t('PartyDetail.inactiveParty'))
                : partyData?.koalicni_strana
                ? capitalizeFirstLetter(t('PartyDetail.coalitionParty'))
                : capitalizeFirstLetter(t('PartyDetail.oppositionParty'));

            return (
                <div className='page-dashboard'>
                    <div className='page-header-row'>
                        <div style={{ '--color-primary': titleColor } as CSSProperties}>
                            <ContentSection
                                title={partyData?.strana_zkratka}
                                level={1}
                                subtitle={subtitleText}
                                className='page-title-section'
                            />
                        </div>
                        <div className='stat-chip-row'>
                            <StatChip
                                icon='description'
                                value={
                                    partyIsActiveWholeTerm
                                        ? capitalizeFirstLetter(t('active', { ns: 'translation' }))
                                        : `${t('x-outOf-y', {
                                              ns: 'translation',
                                              x: partyData.dochazka.mandat,
                                              y: partyData.dochazka.celkem_zasedani
                                          })}`
                                }
                                label={
                                    partyIsActiveWholeTerm
                                        ? `${t('mandateWholeTerm', { ns: 'translation' })}`
                                        : `${t('mandateOfMeetings', {
                                              ns: 'translation',
                                              mandate: partyData.dochazka.mandat,
                                              total: partyData.dochazka.celkem_zasedani,
                                              count: partyData.dochazka.celkem_zasedani
                                          })}`
                                }
                            />
                            <StatChip
                                icon='group'
                                value={partyData.pocet_aktivnich}
                                label={`${t('mandate', { ns: 'translation', count: partyData.pocet_aktivnich })}`}
                                mutedText={allMembersCountString}
                            />
                            <StatChip
                                icon='check_box'
                                value={votingPercentageAttendance}
                                label={t('votingAttendance', { ns: 'translation' })}
                            />
                            <StatChip
                                icon='event_available'
                                value={formatter.percent2From100(partyData.fyzicka_ucast)}
                                label={t('physicalAttendance', { ns: 'translation' })}
                            />
                        </div>
                    </div>
                    {cohesion && (
                        <ContentSection
                            level={3}
                            title={t('PartyDetail.cohesionSectionTitle')}
                            subtitle={t('PartyDetail.cohesionTooltip')}
                            className='dashboard-card'
                        >
                            <div className='stat-chip-row'>
                                <StatChip
                                    icon='balance'
                                    value={cohesion.prumer != null ? formatter.percent2From100(cohesion.prumer) : '—'}
                                    label={t('PartyDetail.cohesion')}
                                    mutedText={cohesionTrendLabel}
                                />
                                {leastCohesiveVoteId != null && leastCohesiveVoteHodnota != null && (
                                    <Link to={councilPath('hlasovani', leastCohesiveVoteId)} className='stat-chip-link'>
                                        <StatChip
                                            icon='gavel'
                                            value={formatter.percent2From100(leastCohesiveVoteHodnota)}
                                            label={t('PartyDetail.leastCohesiveVote')}
                                            mutedText={leastCohesiveVoteLabel}
                                        />
                                    </Link>
                                )}
                            </div>
                            {cohesion.vyvoj.length > 0 && (
                                <NivoLineChart
                                    height='220px'
                                    data={cohesionSeries}
                                    colors={[partyData.strana_barva]}
                                    pointSize={4}
                                    curve='monotoneX'
                                    useMesh
                                    xTitle={t('meetings', { ns: 'meetings' })}
                                    yTitle={t('PartyDetail.cohesionSectionTitle')}
                                    yScale={{ type: 'linear', min: cohesionYMin, max: cohesionYMax }}
                                    axisLeft={{ format: (value) => formatter.percentFrom100(Number(value)) }}
                                    axisBottom={
                                        manyCohesionMeetings
                                            ? {
                                                  tickValues: sparseTickValues(cohesionLabels, 15),
                                                  tickRotation: -40,
                                                  legendOffset: 70,
                                                  format: (value) => truncateLegendLabel(String(value), 10)
                                              }
                                            : undefined
                                    }
                                    margin={{ top: 20, right: 20, bottom: manyCohesionMeetings ? 100 : 50, left: 60 }}
                                    tooltip={({ point }) => (
                                        <BasicTooltip
                                            id={`${t('PartyDetail.cohesionSectionTitle')}: ${formatter.percent2From100(Number(point.data.y))}`}
                                            color={point.seriesColor}
                                            enableChip
                                        />
                                    )}
                                />
                            )}
                        </ContentSection>
                    )}
                    {seatsOverTime.length > 0 && (
                        <ContentSection
                            level={3}
                            title={t('PartyDetail.seatsOverTime')}
                            subtitle={t('PartyDetail.seatsOverTimeTooltip')}
                            className='dashboard-card'
                        >
                            {hasMandateDeviation && mandateAvg !== null && mandateStdDev !== null && (
                                <div className='stat-chip-row'>
                                    <StatChip
                                        icon='chair'
                                        value={formatter.number.format(mandateAvg)}
                                        label={t('PartyDetail.mandateAverage')}
                                        mutedText={`± ${formatter.number.format(mandateStdDev)}`}
                                    />
                                </div>
                            )}
                            <NivoLineChart
                                height='220px'
                                data={seatsSeries}
                                colors={[partyData.strana_barva]}
                                pointSize={6}
                                curve='stepAfter'
                                useMesh
                                xTitle={t('meetings', { ns: 'meetings' })}
                                yTitle={t('PartyDetail.seatsOverTime')}
                                yScale={{ type: 'linear', min: 0, max: maxSeats }}
                                axisLeft={{ tickValues: seatsTickValues, format: (value) => formatter.number.format(Number(value)) }}
                                axisBottom={
                                    manySeatsMeetings
                                        ? {
                                              tickValues: sparseTickValues(seatsLabels, 15),
                                              tickRotation: -40,
                                              legendOffset: 70,
                                              format: (value) => truncateLegendLabel(String(value), 10)
                                          }
                                        : undefined
                                }
                                margin={{ top: 20, right: 20, bottom: manySeatsMeetings ? 100 : 50, left: seatsAxisLeftMargin }}
                                tooltip={({ point }) => {
                                    const index = seatsChangePoints.findIndex((item) => item.zasedani === point.data.x);
                                    const previousSeats = index > 0 ? seatsChangePoints[index - 1].pocet_mandatu : null;
                                    const seats = Number(point.data.y);
                                    const seatsDelta = previousSeats !== null ? seats - previousSeats : null;
                                    const seatsValue = seatsDelta
                                        ? `${formatter.number.format(seats)} (${seatsDelta > 0 ? '+' : ''}${formatter.number.format(seatsDelta)})`
                                        : formatter.number.format(seats);
                                    return (
                                        <TableTooltip
                                            title={<strong>{t('meetingNo', { ns: 'meetings', num: point.data.x })}</strong>}
                                            rows={[
                                                [
                                                    <Chip key='chip' color={point.seriesColor} />,
                                                    t('mandate', { ns: 'translation', count: seats }),
                                                    seatsValue
                                                ]
                                            ]}
                                        />
                                    );
                                }}
                            />
                        </ContentSection>
                    )}
                    {partyData.koalice_historie.length > 0 && (
                        <ContentSection
                            level={3}
                            title={t('PartyDetail.coalitionHistory')}
                            subtitle={t('PartyDetail.coalitionHistoryTooltip')}
                            className='dashboard-card'
                        >
                            <ul className='coalition-history-list'>
                                {partyData.koalice_historie.map((period, index) => (
                                    <li key={index}>{formatDateRange(period.datum_od, period.datum_do, currentLocale, timeZone, t)}</li>
                                ))}
                            </ul>
                        </ContentSection>
                    )}
                    <ContentSection
                        level={3}
                        title={t('PartyDetail.partyVotesSummary')}
                        subtitle={t('PartyDetail.partyVotesSummaryTooltip')}
                        className='dashboard-card'
                    >
                        <VoteBar
                            items={summaryVotesEntries}
                            colors={voteColors}
                            getLabel={getVoteLabel}
                            formatPercent={formatter.percentFrom100}
                        />
                    </ContentSection>
                    <ContentSection
                        level={3}
                        title={t('PartyDetail.agreementWithOtherParties')}
                        subtitle={t('PartyDetail.agreementTooltip')}
                        className='dashboard-card'
                    >
                        {!agreementWithOthers ? (
                            <div className='agreement-list'>
                                <Skeleton height='2rem' style={{ marginBottom: '0.5rem' }} />
                                <Skeleton height='2rem' style={{ marginBottom: '0.5rem' }} />
                                <Skeleton height='2rem' />
                            </div>
                        ) : agreementWithOthers.length > 0 ? (
                            <div className='agreement-list'>
                                {agreementWithOthers.map((item) => (
                                    <div className='agreement-row' key={item.strana_id}>
                                        <span className='agreement-label'>
                                            <i style={{ backgroundColor: item.strana_barva }} />
                                            {item.strana_zkratka}
                                        </span>
                                        <span className='agreement-track'>
                                            <span style={{ width: `${item.shoda}%`, backgroundColor: item.strana_barva }} />
                                        </span>
                                        <span className='agreement-pct'>{formatter.percentFrom100(item.shoda)}</span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className='section-subtitle'>{t('PartyDetail.noAgreementData')}</p>
                        )}
                    </ContentSection>
                    <ContentSection
                        level={3}
                        title={t('members', {ns: 'members'})}
                        subtitle={t('PartyDetail.membersTooltip')}
                        className='dashboard-card'
                    >
                        {activeMembers.length > 0 && (
                            <>
                                <h4>{t('activeCouncilMembers', {ns: 'members'})}</h4>
                                <div className='member-card-list'>
                                    {activeMembers.map((member) => {
                                        const nameParts = member.jmeno.split(' ');
                                        const firstName = nameParts[0];
                                        const lastName = nameParts.slice(1).join(' ');
                                        const currentRole = member.vedeni?.find((position) => position.date_to === null)?.position;

                                        return (
                                            <MemberCard
                                                key={member.id}
                                                memberData={{
                                                    id: member.id,
                                                    firstName: firstName,
                                                    lastName: lastName,
                                                    gender: (member.pohlavi ?? 0) as 0 | 1,
                                                    photo: member.photo
                                                }}
                                                partyData={{
                                                    partyName: partyData.strana_zkratka,
                                                    partyColor: partyData.strana_barva
                                                }}
                                                leadershipRole={currentRole}
                                                customCardContent={
                                                    <MeetingAttendanceBar
                                                        id={String(member.id)}
                                                        percent={member.fyzicka_ucast ?? 0}
                                                        present={member.zasedani_pritomen}
                                                        total={member.zasedani_celkem}
                                                        color={partyData.strana_barva}
                                                        gender={(member.pohlavi ?? 0) as 0 | 1}
                                                    />
                                                }
                                            />
                                        );
                                    })}
                                </div>
                            </>
                        )}
                        {inactiveMembers.length > 0 && (
                            <>
                                <h4 style={{ color: getColors().darkGray, marginTop: '24px' }}>{t('history', {ns: 'translation'})}</h4>
                                <div className='member-card-list'>
                                    {/* // TODO: Move inactive / active in other party to MemberCard component */}
                                    {inactiveMembers.map((member) => {
                                        const nameParts = member.jmeno.split(' ');
                                        const firstName = nameParts[0];
                                        const lastName = nameParts.slice(1).join(' ');

                                        const isNowFor = member.active_member === 1;

                                        const partyName = isNowFor && member.political_party ? member.political_party.shortcut : partyData.strana_zkratka;
                                        const partyColor = isNowFor && member.political_party ? member.political_party.party_color : getColors().darkGray;

                                        return (
                                            <MemberCard
                                                key={member.id}
                                                memberData={{
                                                    id: member.id,
                                                    firstName: firstName,
                                                    lastName: lastName,
                                                    gender: (member.pohlavi ?? 0) as 0 | 1,
                                                    photo: member.photo,
                                                    inactive: member.active_member !== 1
                                                }}
                                                partyData={{
                                                    partyName: partyName,
                                                    partyColor: partyColor,
                                                    isNowFor: isNowFor
                                                }}
                                            />
                                        );
                                    })}
                                </div>
                            </>
                        )}
                    </ContentSection>
                </div>
            );
        }
    }
}

export default PartyDetail;
